/**
 * MutationResultCard.tsx
 *
 * Generic mutation result card — reused by ALL *_mutation envelope kinds:
 * objective_mutation, key_result_mutation, priority_mutation, report_mutation, etc.
 *
 * Shows a tinted success icon-chip + summary, with optional code/name/status chips.
 */

import { CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCopilotMutationSync } from "@/hooks/useCopilotMutationSync";

// ---------------------------------------------------------------------------
// Data shape — present on all mutation envelopes
// ---------------------------------------------------------------------------

export interface MutationData {
  action?: "created" | "updated" | "deleted" | string;
  code?: string;
  name?: string;
  status?: string;
  pid?: string;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface MutationResultCardProps {
  /** Envelope routing key (e.g. "priority_mutation") — drives left-panel cache sync. */
  kind: string;
  summary: string;
  data?: MutationData;
}

export function MutationResultCard({ kind, summary, data }: MutationResultCardProps) {
  useCopilotMutationSync(kind);

  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardContent className="flex items-start gap-2.5 px-4 py-3.5">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-bold leading-tight tracking-tight">{summary}</p>

          {/* Optional metadata chips */}
          {data && (data.action || data.code || data.name || data.status) && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
              {data.action && (
                <Badge variant="secondary" className="rounded-full text-[10.5px] font-semibold px-2 py-0.5 capitalize">
                  {data.action}
                </Badge>
              )}
              {data.code && (
                <Badge variant="outline" className="rounded-full font-mono text-[10.5px] px-2 py-0.5">
                  {data.code}
                </Badge>
              )}
              {data.name && (
                <span className="text-[11px] text-muted-foreground truncate max-w-[160px]">
                  {data.name}
                </span>
              )}
              {data.status && (
                <Badge variant="outline" className="rounded-full text-[10.5px] px-2 py-0.5">
                  {data.status}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
